<script setup lang="ts">
import { testimonials, type Testimonial } from '~/data/testimonials'
import { countryFlag } from '~/utils/country-flag'

const { locale, t } = useI18n()
const { trackEvent } = useAnalytics()

const scrollerRef = ref<HTMLElement | null>(null)

// Current visible card index for dots indicator.
const currentIndex = ref(0)
const isAtStart = ref(true)
const isAtEnd = ref(false)

// Matches `gap-4` (16px) below the `sm` breakpoint and `gap-6` (24px) at ≥sm.
const cardGapPx = () => (typeof window !== 'undefined' && window.innerWidth >= 640 ? 24 : 16)

const formatDate = (yyyymm: string) => {
  // "2024-08" → localised "Aug 2024" / "Авг 2024" / "აგვ 2024"
  const [year, month] = yyyymm.split('-').map(Number)
  if (!year || !month) return yyyymm
  const date = new Date(Date.UTC(year, month - 1, 1))
  return new Intl.DateTimeFormat(locale.value, { month: 'short', year: 'numeric' }).format(date)
}

const quoteFor = (review: Testimonial) => {
  const lang = locale.value as 'en' | 'ru' | 'ka'
  return review.quote[lang] ?? review.quote.en
}

const showTranslationLabel = (review: Testimonial) => {
  return review.originalLanguage !== locale.value &&
    (locale.value === 'en' || locale.value === 'ru' || locale.value === 'ka')
}

const translationLabel = (review: Testimonial) => {
  // Key: testimonials.translated_from.{en|ru} (original language)
  return t(`testimonials.translated_from.${review.originalLanguage}`)
}

const cardStep = () => {
  const card = scrollerRef.value?.querySelector<HTMLElement>('[data-testimonial-card]')
  return card ? card.offsetWidth + cardGapPx() : 0
}

const scroll = (direction: 1 | -1) => {
  if (!scrollerRef.value) return
  const step = cardStep()
  if (!step) return
  scrollerRef.value.scrollBy({ left: direction * step, behavior: 'smooth' })
}

const scrollToIndex = (i: number) => {
  if (!scrollerRef.value) return
  const step = cardStep()
  if (!step) return
  scrollerRef.value.scrollTo({ left: i * step, behavior: 'smooth' })
}

// rAF-throttled scroll handler. The naive version fired on every scroll
// frame and read offsetWidth/clientWidth/scrollWidth + wrote three refs each
// time — this shows up in PSI as "Forced reflow". With rAF coalescing we run
// the layout-read pass at most once per frame and batch all reads before any
// writes (refs trigger Vue updates which can themselves cause reflow).
let scrollFrameId: number | null = null
const onScroll = () => {
  if (scrollFrameId !== null) return
  scrollFrameId = requestAnimationFrame(() => {
    scrollFrameId = null
    const el = scrollerRef.value
    if (!el) return
    const step = cardStep()
    if (!step) return
    // READ phase
    const scrollLeft = el.scrollLeft
    const clientWidth = el.clientWidth
    const scrollWidth = el.scrollWidth
    const atStart = scrollLeft <= 32
    const atEnd = scrollLeft + clientWidth >= scrollWidth - 32
    const nextIndex = atEnd ? testimonials.length - 1 : Math.round(scrollLeft / step)
    // WRITE phase
    isAtStart.value = atStart
    isAtEnd.value = atEnd
    currentIndex.value = nextIndex
  })
}

onMounted(() => {
  scrollerRef.value?.addEventListener('scroll', onScroll, { passive: true })
  window.addEventListener('resize', onScroll)
})
onBeforeUnmount(() => {
  if (scrollFrameId !== null) cancelAnimationFrame(scrollFrameId)
  scrollerRef.value?.removeEventListener('scroll', onScroll)
  window.removeEventListener('resize', onScroll)
})
</script>

<template>
  <section class="relative overflow-hidden py-10 md:py-16 bg-slate-50 dark:bg-slate-800/50">
    <div
      aria-hidden="true"
      class="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-amber-400/60 to-transparent"
    />
    <div
      aria-hidden="true"
      class="pointer-events-none absolute -top-24 left-1/2 h-72 w-72 -translate-x-1/2 rounded-full bg-amber-400/5 blur-3xl dark:bg-amber-400/10"
    />

    <div class="container relative">
      <div class="text-center max-w-2xl mx-auto mb-8">
        <div class="flex items-center justify-center gap-3 font-body text-xs uppercase tracking-[0.22em] text-slate-400 dark:text-slate-500">
          <span class="inline-block h-px w-8 bg-amber-400" />
          <span>{{ $t('testimonials.eyebrow') }}</span>
          <span class="inline-block h-px w-8 bg-amber-400" />
        </div>
        <h2 class="mt-4 font-display italic text-3xl sm:text-4xl md:text-5xl text-balance text-slate-900 dark:text-white">
          {{ $t('testimonials.heading') }}
        </h2>
      </div>

      <div class="relative">
        <!-- Desktop arrow buttons -->
        <button
          v-if="!isAtStart"
          type="button"
          class="hidden md:flex absolute start-0 top-1/2 -translate-y-1/2 -translate-x-4 z-10 size-10 rounded-full bg-white dark:bg-slate-700 shadow-lg items-center justify-center hover:bg-amber-50 dark:hover:bg-slate-600 transition-colors"
          :aria-label="$t('testimonials.carousel.prev')"
          @click="scroll(-1)"
        >
          <Icon name="mdi-chevron-left" size="24" class="text-slate-700 dark:text-white" />
        </button>
        <button
          v-if="!isAtEnd"
          type="button"
          class="hidden md:flex absolute end-0 top-1/2 -translate-y-1/2 translate-x-4 z-10 size-10 rounded-full bg-white dark:bg-slate-700 shadow-lg items-center justify-center hover:bg-amber-50 dark:hover:bg-slate-600 transition-colors"
          :aria-label="$t('testimonials.carousel.next')"
          @click="scroll(1)"
        >
          <Icon name="mdi-chevron-right" size="24" class="text-slate-700 dark:text-white" />
        </button>

        <div
          ref="scrollerRef"
          class="testimonials-scroller flex gap-4 sm:gap-6 overflow-x-auto snap-x snap-mandatory scroll-smooth pb-4 px-3 sm:-mx-4 sm:px-4 motion-reduce:scroll-auto"
          style="scrollbar-width: none"
        >
          <article
            v-for="review in testimonials"
            :key="review.id"
            data-testimonial-card
            class="snap-start flex-none flex flex-col w-[85%] sm:w-[calc(50%-0.75rem)] lg:w-[calc(33.333%-1rem)] rounded-2xl border border-slate-200/80 dark:border-slate-700/60 bg-white dark:bg-slate-900 p-6 shadow-sm"
          >
            <header class="flex items-center gap-3">
              <NuxtImg
                v-if="review.photoPath"
                :src="review.photoPath"
                :alt="review.firstName"
                width="48"
                height="48"
                loading="lazy"
                class="size-12 rounded-full object-cover border border-amber-400/30"
              />
              <div
                v-else
                class="size-12 rounded-full border border-amber-400/30 flex items-center justify-center bg-slate-50 dark:bg-slate-800"
              >
                <span class="font-display italic text-xl text-slate-400 dark:text-slate-500">
                  {{ review.firstName.charAt(0) }}
                </span>
              </div>
              <div class="min-w-0">
                <div class="font-medium text-slate-900 dark:text-white text-sm">
                  {{ review.firstName }}
                </div>
                <div class="text-xs text-slate-500 dark:text-slate-400 truncate">
                  {{ review.location }}
                </div>
              </div>
            </header>

            <div class="mt-4 flex items-center gap-2 font-body text-[11px] uppercase tracking-[0.15em] text-slate-400 dark:text-slate-500 tabular-nums">
              <span class="text-amber-400" aria-hidden="true">★ 5</span>
              <span class="opacity-40" aria-hidden="true">·</span>
              <span>{{ formatDate(review.date) }}</span>
              <span class="opacity-40" aria-hidden="true">·</span>
              <span>{{ countryFlag(review.countryCode) }}</span>
            </div>

            <span class="mt-4 inline-block h-px w-8 bg-amber-400/70" aria-hidden="true" />

            <blockquote class="mt-4 text-slate-700 dark:text-slate-300 text-sm leading-relaxed">
              &ldquo;{{ quoteFor(review) }}&rdquo;
            </blockquote>

            <p
              v-if="showTranslationLabel(review)"
              class="mt-3 text-xs italic text-slate-400 dark:text-slate-500"
            >
              {{ translationLabel(review) }}
            </p>

            <a
              :href="review.airbnbProfileUrl"
              target="_blank"
              rel="noopener noreferrer"
              class="mt-auto pt-5 self-start inline-flex items-center gap-1 text-sm text-amber-600 dark:text-amber-400 hover:text-amber-700 dark:hover:text-amber-300 font-medium transition-colors"
              @click="trackEvent('airbnb_click', { placement: 'review', review_id: review.id })"
            >
              {{ $t('testimonials.read_on_airbnb') }}
              <Icon name="mdi-arrow-top-right" size="16" class="align-baseline" />
            </a>
          </article>
        </div>

        <!-- Mobile + tablet dots indicator. The dots are 8 px tall but the
             button itself is 44×44 (transparent padding) so touch targets
             clear WCAG AA. -->
        <div class="flex justify-center mt-2">
          <button
            v-for="(_, i) in testimonials"
            :key="i"
            type="button"
            class="inline-flex items-center justify-center size-11"
            :aria-label="$t('testimonials.carousel.goToReview', { number: i + 1 })"
            @click="scrollToIndex(i)"
          >
            <span
              class="block h-2 rounded-full transition-all"
              :class="i === currentIndex
                ? 'w-6 bg-amber-400'
                : 'w-2 bg-slate-300 dark:bg-slate-600 hover:bg-slate-400'"
              aria-hidden="true"
            />
          </button>
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped>
/* Hide scrollbar while keeping scroll functional */
.testimonials-scroller::-webkit-scrollbar {
  display: none;
}
</style>
